import { Group, ScrollArea, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import { InitiativeSource } from '../../../../api/initiative-client/initiative-client.type';
import {
  useBookmarkGoalMutation,
  useBookmarkOutcomeMutation,
  useUnbookmarkGoalMutation,
  useUnbookmarkOutcomeMutation,
} from '../../../../api/strategy-client/strategy-client.hooks';
import type { Goal, Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import {
  resetRelatedTasksTabState,
  setActivePanel,
  setSelectedGoal,
  setSelectedInitiative,
  setSelectedOutcome,
} from '../../../../store/strategy-store/strategy-store.actions';
import { baseWhite, primaryBase, purpleBase, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { usePartiallyUpdateInitiative } from '../../initiative-performance/initiative-performance.hooks';
import { getSourceIcon } from '../main-section/main-section.helpers';
import { SideSectionInfo } from './side-section-info';
import { ProgressBar } from './side-section-progress';

type EntityType = 'initiative' | 'outcome' | 'goal';

type Props = {
  entity: Initiative | Outcome | Goal;
  entityType: EntityType;
  onClose?: () => void;
};

// Type guards
function isInitiative(entity: Initiative | Outcome | Goal): entity is Initiative {
  return 'portfolio' in entity;
}

export const SideSectionSelectedEntity = ({ entity, entityType, onClose }: Props) => {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const portfolio = useGlobalStore((state) => state.portfolio);

  // Bookmark mutations for different entity types
  const editInitiativeMutation = usePartiallyUpdateInitiative({
    portfolioId: portfolio?.id,
    initiativeId: isInitiative(entity) ? entity.id : undefined,
  });

  const bookmarkGoalMutation = useBookmarkGoalMutation();
  const unbookmarkGoalMutation = useUnbookmarkGoalMutation();
  const bookmarkOutcomeMutation = useBookmarkOutcomeMutation();
  const unbookmarkOutcomeMutation = useUnbookmarkOutcomeMutation();

  if (!entity || !portfolio) {
    return null;
  }

  // Get entity-specific display values
  const percentProgress = 'percent_progress' in entity ? entity.percent_progress : 0;
  const percentElapsed = 'percent_elapsed' in entity ? entity.percent_elapsed : 0;
  const isBookmarked = entity.is_bookmarked || false;
  const canBookmark = true;
  const displayKey = entity.key || 'Unknown Key';
  const entitySource: string = entity.source || 'bloomfilter';

  const handleKeyClick = () => {
    if (entitySource !== 'bloomfilter' && 'url' in entity && entity.url) {
      window.open(entity.url, '_blank', 'noopener, noreferrer');
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (entityType === 'initiative') {
      setSelectedInitiative(null);
    } else if (entityType === 'outcome') {
      setSelectedOutcome(null);
    } else {
      setSelectedGoal(null);
    }
    setActivePanel(null);
  };

  const handleViewDetails = () => {
    resetRelatedTasksTabState();

    // Route based on entity type
    let route: string;
    if (entityType === 'initiative') {
      route = `/application/strategy/${portfolio.id}/${entity.id}`;
    } else if (entityType === 'goal') {
      route = `/application/strategy/goals/${entity.id}`;
    } else {
      route = `/application/strategy/outcomes/${entity.id}`;
    }

    navigate(route);
  };

  // Get button text based on entity type
  const getButtonText = () => {
    switch (entityType) {
      case 'initiative':
        return i18n.t('strategy.initiatives.more_initiative_details');
      case 'goal':
        return 'More Goal Details';
      case 'outcome':
        return 'More Objective Details';
      default:
        return 'More Details';
    }
  };

  return (
    <Stack h="100%">
      <Group justify="space-between">
        <Group
          gap={4}
          style={{
            cursor: entitySource !== 'bloomfilter' && 'url' in entity && entity.url ? 'pointer' : 'default',
          }}
          onClick={handleKeyClick}
        >
          <img src={getSourceIcon(entitySource as InitiativeSource)} width={16} height={16} alt={entitySource} />
          <Text weight="bold" size="medium" color={secondaryBase}>
            {displayKey}
          </Text>
          {entitySource !== 'bloomfilter' && 'url' in entity && entity.url && (
            <Tooltip variant="white" label={i18n.t('common.visit_source_page')} position="right">
              <Icon name="arrow_outward" size={16} color={secondaryBase} pointer />
            </Tooltip>
          )}
        </Group>
        <Group gap={4}>
          {canBookmark &&
            (() => {
              let isPending = false;
              if (entityType === 'initiative') {
                isPending = editInitiativeMutation.isPending;
              } else if (entityType === 'goal') {
                isPending = isBookmarked ? unbookmarkGoalMutation.isPending : bookmarkGoalMutation.isPending;
              } else if (entityType === 'outcome') {
                isPending = isBookmarked ? unbookmarkOutcomeMutation.isPending : bookmarkOutcomeMutation.isPending;
              }

              return isPending ? (
                <Loader size="xs" />
              ) : (
                <Icon
                  name={isBookmarked ? 'bookmark_added' : 'bookmark_add'}
                  size={20}
                  color={isBookmarked ? secondaryBase : skyDark}
                  pointer
                  onClick={() => {
                    if (entityType === 'initiative') {
                      editInitiativeMutation.mutateAsync({ is_bookmarked: !isBookmarked });
                    } else if (entityType === 'goal') {
                      if (isBookmarked) {
                        unbookmarkGoalMutation.mutate(entity.id, {
                          onSuccess: (updatedGoal) => setSelectedGoal(updatedGoal),
                        });
                      } else {
                        bookmarkGoalMutation.mutate(entity.id, {
                          onSuccess: (updatedGoal) => setSelectedGoal(updatedGoal),
                        });
                      }
                    } else if (entityType === 'outcome') {
                      if (isBookmarked) {
                        unbookmarkOutcomeMutation.mutate(entity.id, {
                          onSuccess: (updatedOutcome) => setSelectedOutcome(updatedOutcome),
                        });
                      } else {
                        bookmarkOutcomeMutation.mutate(entity.id, {
                          onSuccess: (updatedOutcome) => setSelectedOutcome(updatedOutcome),
                        });
                      }
                    }
                  }}
                />
              );
            })()}

          <Icon name="close" onClick={handleClose} size={20} color={skyDark} aria-label="Close panel" />
        </Group>
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0}>
        <Stack>
          <Text weight="medium" size="medium">
            {entity.name}
          </Text>
          <Divider />
          <ProgressBar
            value={percentProgress}
            label={i18n.t('strategy.initiatives.table.completion')}
            color={primaryBase}
          />
          <ProgressBar
            value={percentElapsed}
            label={i18n.t('strategy.initiative_performance.time_elapsed')}
            color={purpleBase}
          />
          <SideSectionInfo entity={entity} entityType={entityType} />
          <Divider />
        </Stack>
      </ScrollArea>
      <Button
        variant="secondary"
        size="xs"
        radius="xl"
        fullWidth
        leftSection={<Icon name="remove_red_eye" variant="filled" color={baseWhite} size={14} pointer />}
        onClick={handleViewDetails}
      >
        <Text size="small" color={baseWhite}>
          {getButtonText()}
        </Text>
      </Button>
    </Stack>
  );
};
